// Figma: https://www.figma.com/design/nykbXADUTliE6d7xv6FjcQ/NEW-TSAB-Shared-Customer-Library?node-id=44-965
// (button base component — outlined variant: 46-838, primary variant: 46-842)
// Modal alert buttons: node-id=209-5177

import classnames from 'classnames'
import { SPINNER_SVG, BACK_ARROW_SVG } from '~/lib/buttonSvgs'

export default function ButtonDrawer({ buttons, isLoading, className, children, fullscreen, modal }) {
  // Modal variant: flush buttons at the bottom of the alert card, per Figma 209-5177.
  // No padding/border-radius — overflow:clip on the container clips the corners.
  // border-t separates buttons from content; border-r divides two side-by-side buttons.
  if (modal) {
    return (
      <div className={classnames(
        'flex flex-row border-0 border-t-[1.5px] border-solid border-[#17181a]',
        { 'pointer-events-none': isLoading },
      )}>
        {buttons.map((button, index) => {
          const isPrimary = index === buttons.length - 1
          const isButtonLoading = button.isLoading === true
          const hasDivider = index < buttons.length - 1

          return (
            <button
              key={index}
              type="button"
              tabIndex={button.tabIndex !== undefined ? button.tabIndex : 0}
              onClick={button.func}
              disabled={button?.disabled === true}
              className={classnames(
                'flex-1 py-4 px-3 text-center',
                'font-sans text-[16px] font-semibold uppercase tracking-[0.68px] leading-[1.2]',
                'transition-colors duration-200',
                'disabled:opacity-50 disabled:cursor-default disabled:pointer-events-none',
                hasDivider && 'border-0 border-r-[1.5px] border-solid border-[#17181a]',
                isPrimary
                  ? 'bg-[#17181a] text-white hover:opacity-90'
                  : 'bg-transparent text-[#17181a] hover:bg-black/5',
                isButtonLoading && 'cursor-default !text-transparent select-none bg-no-repeat bg-center bg-[length:24px_24px]',
              )}
              style={{ backgroundImage: isButtonLoading ? SPINNER_SVG : undefined }}
              dangerouslySetInnerHTML={{ __html: button.title }}
            />
          )
        })}
      </div>
    )
  }

  // Regular flow variant: rounded buttons in a white padded drawer
  return (
    <div
      className={classnames(
        'bg-white p-2 app-lg:rounded-b-[12px]',
        { 'pointer-events-none': isLoading },
        { 'app-lg:absolute app-lg:bottom-0 app-lg:left-0 app-lg:w-full': fullscreen },
        className,
      )}
    >
      {children}
      <div className="flex flex-row flex-wrap gap-2 items-start">
        {buttons.map((button, index) => {
          const isPrimary = index === buttons.length - 1
          const isBack = button.isBack === true
          const isButtonLoading = button.isLoading === true
          const isHalfWidth = buttons.length === 4

          return (
            <button
              key={index}
              type="button"
              tabIndex={button.tabIndex !== undefined ? button.tabIndex : 0}
              onClick={button.func}
              disabled={button?.disabled === true}
              className={classnames(
                'whitespace-nowrap h-12 px-[25px] rounded-button border-solid',
                'font-sans text-[16px] font-semibold uppercase tracking-[0.11em] leading-[1.1]',
                'transition-colors duration-400 grow',
                'disabled:cursor-default disabled:pointer-events-none',

                isPrimary
                  ? [
                      'border border-primary bg-primary text-white',
                      'hover:bg-[rgba(0,0,0,0.7)] focus-visible:bg-[rgba(0,0,0,0.7)]',
                      '[flex-grow:7]',
                      'disabled:border-[#d0cfcf] disabled:bg-surface-container-highest disabled:text-[#a7a7a8]',
                    ]
                  : [
                      'border border-primary bg-white text-primary',
                      'hover:bg-black/20 focus-visible:bg-black/20',
                      'disabled:border-[#d0cfcf] disabled:text-[#a7a7a8]',
                    ],

                isBack && 'shrink-0 !w-12 max-w-[48px] bg-no-repeat bg-center grow-0',
                isButtonLoading && 'cursor-default !text-transparent select-none bg-no-repeat bg-center bg-[length:24px_24px]',
                isHalfWidth && 'basis-[calc(50%-4px)]',
              )}
              style={{
                backgroundImage: isBack ? BACK_ARROW_SVG : isButtonLoading ? SPINNER_SVG : undefined,
              }}
              dangerouslySetInnerHTML={{ __html: button.title }}
            />
          )
        })}
      </div>
    </div>
  )
}
